import { Broadcast } from "@switcherstudio/switcher-api-client";
import {
    DestinationProcessingStatus,
    useDestinationConnections
} from "hooks/useDestinationConnections";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import CheckmarkIcon from "assets/icons/checkmark-filled.svg?react";
import ErrorIcon from "assets/icons/exclamation.svg?react";
import { useTranslation } from "react-i18next";
import { ProcessingDestination } from "components/modal/NewLivestreamModal/steps/Processing/ProcessingDestination";
import { motion } from "framer-motion";
// Reuse the wizard's own Processing styles instead of duplicating them.
import styles from "components/modal/NewLivestreamModal/steps/Processing/index.module.scss";
import { AddDestinationsSubmissionStatus } from "../../types";

interface AddDestinationsProcessingStepProps {
    // Newly selected destinations - already excludes whatever was seeded from the broadcast.
    destinations: DestinationDescriptor[];
    broadcast: Broadcast;
    onStatusChange: (status: AddDestinationsSubmissionStatus) => void;
    // Called once all in-flight connection requests settle, even if the modal already closed.
    onSettled?: () => void;
}

export const AddDestinationsProcessingStep = ({
    destinations,
    broadcast,
    onStatusChange,
    onSettled
}: AddDestinationsProcessingStepProps) => {
    const { t } = useTranslation();

    const {
        postStatuses,
        postErrors,
        withStatus,
        seedDestinationForms,
        processDestination,
        retryDestination
    } = useDestinationConnections();

    const [hasStarted, setHasStarted] = useState(false);

    // Only refetch on settle if the step already unmounted (cancelled mid-flight) -
    // refetching while still mounted would reshuffle `destinations` via the parent's
    // existingDestinationIds and falsely show a Mixed/error result on a real success.
    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const settleIfUnmounted = useCallback(() => {
        if (!isMountedRef.current) onSettled?.();
    }, [onSettled]);

    const runSubmission = useCallback(async () => {
        setHasStarted(true);
        seedDestinationForms(destinations);
        const requests = destinations.map((d) =>
            withStatus(d.id, () => processDestination(d, broadcast))
        );
        await Promise.allSettled(requests).finally(settleIfUnmounted);
    }, [
        destinations,
        seedDestinationForms,
        withStatus,
        processDestination,
        broadcast,
        settleIfUnmounted
    ]);

    useEffect(() => {
        runSubmission();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRetryDestination = useCallback(
        (destId: string) => {
            const destination = destinations.find((d) => d.id === destId);
            if (!destination) return;
            retryDestination(destination, broadcast).finally(
                settleIfUnmounted
            );
        },
        [destinations, broadcast, retryDestination, settleIfUnmounted]
    );

    const { allSuccessful, allWrong, stillProcessing } = useMemo(() => {
        const statuses = destinations.map(
            (d) => postStatuses[d.id] ?? DestinationProcessingStatus.Processing
        );

        return {
            stillProcessing: statuses.some(
                (s) => s === DestinationProcessingStatus.Processing
            ),
            allSuccessful:
                statuses.length > 0 &&
                statuses.every((s) => s === DestinationProcessingStatus.Success),
            allWrong:
                statuses.length > 0 &&
                statuses.every((s) => s === DestinationProcessingStatus.Error)
        };
    }, [destinations, postStatuses]);

    useEffect(() => {
        if (!hasStarted) return;

        onStatusChange(
            stillProcessing
                ? AddDestinationsSubmissionStatus.Processing
                : allSuccessful
                ? AddDestinationsSubmissionStatus.Success
                : AddDestinationsSubmissionStatus.Mixed
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasStarted, stillProcessing, allSuccessful]);

    const headerContent = useMemo(() => {
        if (stillProcessing) {
            return (
                <>
                    <h6>{t("add-destinations-modal:connecting-title")}</h6>
                    <p>{t("add-destinations-modal:connecting-subtitle")}</p>
                </>
            );
        }

        if (allSuccessful) {
            return (
                <>
                    <CheckmarkIcon className={styles.checkmark} />
                    <h6>{t("add-destinations-modal:success-title")}</h6>
                    <p>{t("add-destinations-modal:success-subtitle")}</p>
                </>
            );
        }

        if (allWrong) {
            return (
                <>
                    <ErrorIcon className={styles.exclamation} />
                    <h6>{t("add-destinations-modal:error-title")}</h6>
                    <p>{t("add-destinations-modal:error-subtitle")}</p>
                </>
            );
        }

        return (
            <>
                <h6>{t("add-destinations-modal:mixed-title")}</h6>
                <p>{t("add-destinations-modal:mixed-subtitle")}</p>
            </>
        );
    }, [stillProcessing, allSuccessful, allWrong, t]);

    const orderedDestinations = useMemo(() => {
        return [...destinations].sort((a, b) => {
            const statusOrder = {
                [DestinationProcessingStatus.Success]: 0,
                [DestinationProcessingStatus.Error]: 1,
                [DestinationProcessingStatus.Processing]: 2
            };
            const statusA =
                postStatuses[a.id] ?? DestinationProcessingStatus.Processing;
            const statusB =
                postStatuses[b.id] ?? DestinationProcessingStatus.Processing;

            if (statusA === statusB) {
                return a.name.localeCompare(b.name);
            }

            return statusOrder[statusA] - statusOrder[statusB];
        });
    }, [destinations, postStatuses]);

    return (
        <section className={styles["processing-step"]}>
            <header>{headerContent}</header>

            {orderedDestinations.length > 0 && (
                <motion.ul
                    className={styles["destination-list"]}
                    layout
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                >
                    {orderedDestinations.map((destination) => (
                        <motion.li
                            key={destination.id}
                            layout
                            transition={{ duration: 0.35, ease: "easeInOut" }}
                        >
                            <ProcessingDestination
                                destination={destination}
                                status={
                                    postStatuses[destination.id] ??
                                    DestinationProcessingStatus.Processing
                                }
                                error={postErrors[destination.id]}
                                onRetry={handleRetryDestination}
                            />
                        </motion.li>
                    ))}
                </motion.ul>
            )}
        </section>
    );
};
